import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { devLog } from "@/lib/log";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // CRITICAL: If returning from Emergent OAuth, skip the /auth/session check —
    // AuthCallback will exchange the session_id and establish cookies first,
    // then re-run refresh.
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      // /auth/session ALWAYS returns 200 — anonymous → { authenticated: false }.
      // This keeps the console clean; /auth/me still throws 401 for guarded flows.
      const { data } = await api.get("/auth/session");
      if (data?.authenticated && data.user) {
        setUser(data.user);
      } else {
        setUser(false);
      }
    } catch {
      // Only network / server errors reach here — treat as anonymous.
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Global session-expired signal — any protected endpoint returning 401 (see
  // api.js response interceptor) drops the user to anon so Protected bounces
  // to /login. Cheap and idempotent.
  useEffect(() => {
    const onExpired = () => { setUser(false); };
    if (typeof window !== "undefined") {
      window.addEventListener("bi-session-expired", onExpired);
      return () => window.removeEventListener("bi-session-expired", onExpired);
    }
    return undefined;
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      // Fresh signup on this device → clear any legacy device-wide install-prompt
      // dismissal so the AddToHomeScreen nudge can appear again for this account.
      try {
        localStorage.removeItem("buildintel_a2hs_dismissed_at");
      } catch (err) {
        devLog.debug("localStorage clear (a2hs) failed", err);
      }
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      devLog.warn("logout call failed", e);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
